import type {
  AsyncDialogFactory,
  AsyncDialogOptions,
} from 'components/core/Dialog/common/useAsyncDialog';
import { isArchiveOperation } from 'shared/domain/documentation/operationTypeGuards';
import type {
  ConfirmPreparedOperationResult,
  PreparedDocumentationOperation,
} from 'store/documentation/slices/operations/types';
import { documentationDeleteAsyncDialog } from './documentationDeleteDialog';
import { documentationRestoreAsyncDialog } from './documentationRestoreDialog';

/** Confirmation dialog for a prepared archive or restore operation. */
export function createDocumentationOperationDialog(
  prepared: PreparedDocumentationOperation
): AsyncDialogOptions {
  return isArchiveOperation(prepared.operationType)
    ? documentationDeleteAsyncDialog(prepared.resolvedCount, prepared.selectedItemsBreakdown)
    : documentationRestoreAsyncDialog(
        prepared.resolvedDocumentations.length,
        prepared.resolvedDirectories.length,
        prepared.selectedItemsBreakdown
      );
}

interface ConfirmDocumentationOperationParams {
  prepared: PreparedDocumentationOperation;
  createAsyncDialog: AsyncDialogFactory;
  confirmPreparedOperation: () => Promise<ConfirmPreparedOperationResult>;
  /** Latest prepared operation - refreshed by the store when the first confirmation was stale. */
  getPreparedOperation: () => PreparedDocumentationOperation | null;
  clearPreparedOperation: () => void;
}

/**
 * Asks to confirm a prepared archive/restore. When the selection changed in the
 * meantime (`stale`), asks again with the refreshed counts. Returns the result,
 * or `undefined` when the user cancelled (the prepared operation is cleared).
 */
export async function confirmDocumentationOperation({
  prepared,
  createAsyncDialog,
  confirmPreparedOperation,
  getPreparedOperation,
  clearPreparedOperation,
}: ConfirmDocumentationOperationParams): Promise<ConfirmPreparedOperationResult | undefined> {
  try {
    const result = await createAsyncDialog(
      createDocumentationOperationDialog(prepared),
      confirmPreparedOperation
    );
    if (!isStaleOperationResult(result)) {
      return result;
    }

    const refreshed = getPreparedOperation();
    if (!refreshed || refreshed.resolvedCount === 0) {
      return result;
    }
    return await createAsyncDialog(
      createDocumentationOperationDialog(refreshed),
      confirmPreparedOperation
    );
  } catch {
    clearPreparedOperation();
    return undefined;
  }
}

export function isExecutedOperationResult(
  result: ConfirmPreparedOperationResult | undefined
): boolean {
  return result?.status === 'executed';
}

function isStaleOperationResult(result: ConfirmPreparedOperationResult): boolean {
  return result.status === 'stale';
}
